import store from '../plugins/vuex'
import UserSolution from '../models/UserSolution'

class PermissionsService
{

  can(permission: string, ...params: any[]): boolean {

    if (this.#userHasRole('super_admin')) {
      return true
    }

    const permissionParts = permission.split('.').map((part: string) => {
      return part.substr(0, 1).toUpperCase() + part.substr(1) // .toLowerCase()
    })

    const method = 'can' + permissionParts.join('')

    if (!(method in this)) {
      console.error('PermissionsService has no method named ' + method)
      return false
    } else {
      // @ts-ignore: Don't complain that params isn't a tuple
      return this[method as keyof PermissionsService](...params)
    }

  }

  canDashboardView(): boolean {
    return this.#userHasRole('super_admin')
  }

  canBlackoutsRead(locationID: number): boolean {
    return this.#getUserSolution(locationID, 'blackoutsRead')
  }

  canBlackoutsWrite(locationID: number): boolean {
    // if (this.#userHasRole(['super_admin'])) {
    //   return true
    // }
    return this.#getUserSolution(locationID, 'blackoutsWrite')
  }

  // Can this user view inventory data for the specified location?
  canUniversalInventoryRead(locationID: number): boolean {
    return this.#getUserSolution(locationID, 'universalInventoryRead')
  }

  // Can this user create/edit/delete inventory data for the specified location?
  canUniversalInventoryWrite(locationID: number): boolean {
    return this.#getUserSolution(locationID, 'universalInventoryWrite')
  }

  // Can this user create, edit, and delete locations (and associated data such as LocationSolutions and LocationProducts)?
  // (Note that access to the Location > Venues tab is controlled by a separate permission, canVlpsRead().)
  canLocationsWrite(): boolean {
    return this.#userHasRole(['super_admin'])
  }

  canArchivedlocationsRead(): boolean {
    return this.#userHasRole(['super_admin'])
  }

  canLocationInternalNotesRead(): boolean {
    return this.#userHasRole(['super_admin'])
  }

  canLocationInternalNotesWrite(): boolean {
    return this.#userHasRole(['super_admin'])
  }

  canLocationProductsWrite(): boolean {
    return this.#userHasRole(['super_admin'])
  }

  // Does this user have full read/write access to the Locations > URI page?
  canLocationsUri(locationID: number): boolean {
    return this.#getUserSolution(locationID, 'uriWrite')
  }

  // Can this user access the Location > Venues tab (in read-only mode)?
  canVlpsRead(): boolean {
    return true
  }

  // Can this user access the Location > Venues tab (in edit mode)?
  canVlpsWrite(): boolean {
    return this.#userHasRole(['super_admin'])
  }

  // This is a very powerful permission! It means that a user can:
  // - Perform all CRUD operations on all organizations
  // - See and edit org-level records (Locations, Products, etc.) for all organizations
  canOrganizationsManage(): boolean {
    return this.#userHasRole('super_admin')
  }

  canProductsRead(): boolean {
    return this.#userHasRole(['super_admin', 'admin'])
  }

  canProductsWrite(): boolean {
    return this.#userHasRole(['super_admin'])
  }

  canRateManagementRead(locationID: number): boolean {
    return this.#getUserSolution(locationID, 'rateManagementRead')
  }

  canRateManagementWrite(locationID: number): boolean {
    return this.#getUserSolution(locationID, 'rateManagementWrite')
  }

  // Can this user view reports for the specified location?
  canReportingRead(locationID: number): boolean {
    return this.#getUserSolution(locationID, 'reportingRead')
  }

  canReportingfornonlivelocationsRead(): boolean {
    return this.#userHasRole(['super_admin'])
  }

  // Should IDs be displayed to this user?
  canSeeids(): boolean {
    return this.#userHasRole(['super_admin'])
  }

  // Can this user view the /users page (and perform all actions therein)?
  canUsersIndex() {
    return this.#userHasRole(['super_admin', 'admin'])
  }

  /**
   * Private methods
   */

  #getUser() {
    return store.state.auth.user
  }

  #getUserSolution(locationID: number, property: UserSolutionPermissions) {

    const user = this.#getUser()

    const userSolution = UserSolution.query()
      .where('userID', user.ID)
      .where('locationID', locationID)
      .first()

    return userSolution ? userSolution[property] : false

  }

  /**
   * roles can be either a string (e.g., 'super_admin'), or an array (e.g.,
   * ['super_admin', 'admin']).
   */
  #userHasRole(roles: string | string[]): boolean {

    if (!Array.isArray(roles)) {
      roles = [roles]
    }

    let hasRole = false
    const user = this.#getUser()
    if (user) {
      hasRole = roles.includes(user.role)
    }
    return hasRole
  }

}

export default new PermissionsService()

export type UserSolutionPermissions = (
  'universalInventoryRead' |
  'universalInventoryWrite' |
  'rateManagementRead' |
  'rateManagementWrite' |
  'blackoutsRead' |
  'blackoutsWrite' |
  'reportingRead' |
  'reportingWrite' |
  'rateParityRead' |
  'rateParityWrite' |
  'maxCapacityRead' |
  'maxCapacityWrite' |
  'uriRead' |
  'uriWrite'
  )
